/**
 * Payment Profile Form Component
 *
 * A reusable component for managing user payment profile with:
 * - IBAN validation (Swiss format)
 * - Form for bank account details
 * - Loading and error states
 * - Empty state for new users
 * - Pre-populated state for existing profiles
 */

import { apiClient } from '../services/api-client';
import { renderIcon, createSpinner } from '../utils/icons';
import { showToast } from '../components/toast';
import { createInput } from '../components/input';
import { createSelect } from '../components/select';
import { FormField } from '../components/form-field';

// ============================================================================
// Types
// ============================================================================

/**
 * User payment profile from backend
 */
export interface UserPaymentProfile {
  id: string;
  user_id: string;
  iban: string;
  swift_bic: string | null;
  account_holder_name: string;
  street_address: string | null;
  postal_code: string | null;
  city: string | null;
  country: string;
  created_at: string;
  updated_at: string;
}

/**
 * API response type for payment profile
 */
interface PaymentProfileResponse {
  data: UserPaymentProfile;
  message: string;
}

/**
 * IBAN validation result
 */
interface IbanValidationResult {
  valid: boolean;
  message?: string;
}

// ============================================================================
// IBAN Validation
// ============================================================================

/**
 * Swiss IBAN pattern: CH + 2 check digits + 17 alphanumeric characters
 * Total: 21 characters
 */
const SWISS_IBAN_PATTERN = /^CH\d{2}[A-Z0-9]{17}$/i;

/**
 * Normalize IBAN by removing spaces and converting to uppercase
 */
export function normalizeIban(iban: string): string {
  return iban.replace(/\s/g, '').toUpperCase();
}

/**
 * Validate IBAN checksum using MOD 97 algorithm (ISO 13616)
 * Returns true if the checksum is valid
 */
export function validateIbanChecksum(iban: string): boolean {
  const normalized = normalizeIban(iban);

  // Move first 4 characters to the end
  const rearranged = normalized.slice(4) + normalized.slice(0, 4);

  // Convert letters to numbers (A=10, B=11, ..., Z=35)
  let numericString = '';
  for (const char of rearranged) {
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Calculate MOD 97 using chunks to avoid BigInt issues
  let remainder = 0;
  for (let i = 0; i < numericString.length; i += 7) {
    const chunk = remainder.toString() + numericString.slice(i, i + 7);
    remainder = parseInt(chunk, 10) % 97;
  }

  return remainder === 1;
}

/**
 * Validate a Swiss IBAN
 */
export function validateIban(iban: string): IbanValidationResult {
  const normalized = normalizeIban(iban);

  if (!normalized) {
    return { valid: false, message: 'IBAN is required' };
  }

  if (!SWISS_IBAN_PATTERN.test(normalized)) {
    return {
      valid: false,
      message: 'Invalid Swiss IBAN format. Must start with CH followed by 19 alphanumeric characters.',
    };
  }

  // Validate checksum
  if (!validateIbanChecksum(normalized)) {
    return {
      valid: false,
      message: 'Invalid IBAN checksum. Please verify your IBAN is correct.',
    };
  }

  return { valid: true };
}

// ============================================================================
// Debounce Utility
// ============================================================================

/**
 * Simple debounce function for input validation
 */
function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

// ============================================================================
// Country Options
// ============================================================================

const COUNTRY_OPTIONS = [
  { value: 'CH', label: 'Switzerland' },
  { value: 'DE', label: 'Germany' },
  { value: 'AT', label: 'Austria' },
  { value: 'FR', label: 'France' },
  { value: 'IT', label: 'Italy' },
  { value: 'LI', label: 'Liechtenstein' },
];

// ============================================================================
// Payment Profile Form Class
// ============================================================================

export class PaymentProfileForm {
  private container: HTMLElement;
  private profile: UserPaymentProfile | null = null;
  private ibanField: FormField | null = null;
  private onSaveCallback: ((profile: UserPaymentProfile) => void) | undefined;

  constructor(container: HTMLElement, options?: { onSave?: (profile: UserPaymentProfile) => void }) {
    this.container = container;
    this.onSaveCallback = options?.onSave;
  }

  /**
   * Initialize and render the form
   */
  public async init(): Promise<void> {
    this.renderLoading();
    await this.loadProfile();
    this.render();
  }

  /**
   * Load the user's payment profile from API
   */
  private async loadProfile(): Promise<void> {
    try {
      const response = await apiClient.get<PaymentProfileResponse>('/payment-profiles/me');
      this.profile = response.data.data;
    } catch (error) {
      const err = error as { status?: number; message?: string };
      if (err.status === 404) {
        // No profile exists yet - that's fine
        this.profile = null;
      } else {
        throw error; // Re-throw other errors
      }
    }
  }

  /**
   * Render loading state
   */
  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="flex items-center justify-center p-8">
        <div class="flex items-center gap-3">
          ${renderIcon('loader', { size: 'md', className: 'animate-spin text-primary' }).outerHTML}
          <p class="text-muted-foreground">Loading payment profile...</p>
        </div>
      </div>
    `;
  }

  /**
   * Render the form
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="space-y-6">
        <div>
          <h2 class="text-lg font-semibold text-foreground mb-2">Bank Account Details</h2>
          <p class="text-sm text-muted-foreground mb-4">
            Enter your bank account details for receiving expense reimbursements.
          </p>
        </div>

        <form id="payment-profile-form" class="space-y-6">
          <div class="space-y-6" id="form-fields-container">
            <!-- Fields will be added here programmatically -->
          </div>

          <div class="flex justify-end">
            <button
              type="submit"
              class="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              id="save-button"
            >
              ${renderIcon('save', { size: 'sm' }).outerHTML}
              <span>Save Profile</span>
            </button>
          </div>
        </form>
      </div>
    `;

    this.renderFormFields();
    this.attachEventListeners();
  }

  /**
   * Render form fields programmatically
   */
  private renderFormFields(): void {
    const container = this.container.querySelector('#form-fields-container');
    if (!container) return;

    // IBAN Field (required)
    const ibanInputElement = createInput({
      name: 'iban',
      type: 'text',
      placeholder: 'CH93 0076 2011 6238 5295 7',
      required: true,
      value: this.profile?.iban || '',
      autocomplete: 'off',
    });

    this.ibanField = new FormField(ibanInputElement, {
      label: { text: 'IBAN' },
      required: true,
      helperText: { text: 'Swiss IBAN format: CH + 19 characters', type: 'default' },
    });
    container.appendChild(this.ibanField.getElement());

    // SWIFT/BIC Field (optional)
    const swiftInput = createInput({
      name: 'swiftBic',
      type: 'text',
      placeholder: 'POFICHBEXXX',
      value: this.profile?.swift_bic || '',
      autocomplete: 'off',
    });

    const swiftField = new FormField(swiftInput, {
      label: { text: 'SWIFT/BIC' },
      helperText: { text: 'Optional - 8 or 11 characters', type: 'default' },
    });
    container.appendChild(swiftField.getElement());

    // Account Holder Name (required)
    const holderInput = createInput({
      name: 'accountHolderName',
      type: 'text',
      placeholder: 'John Doe',
      required: true,
      value: this.profile?.account_holder_name || '',
      autocomplete: 'name',
    });

    const holderField = new FormField(holderInput, {
      label: { text: 'Account Holder Name' },
      required: true,
    });
    container.appendChild(holderField.getElement());

    // Street Address (optional)
    const streetInput = createInput({
      name: 'streetAddress',
      type: 'text',
      placeholder: '123 Main Street',
      value: this.profile?.street_address || '',
      autocomplete: 'street-address',
    });

    const streetField = new FormField(streetInput, {
      label: { text: 'Street Address' },
    });
    container.appendChild(streetField.getElement());

    // Postal Code + City (side by side)
    const postalCityRow = document.createElement('div');
    postalCityRow.className = 'grid grid-cols-2 gap-4';

    // Postal Code
    const postalInput = createInput({
      name: 'postalCode',
      type: 'text',
      placeholder: '8001',
      value: this.profile?.postal_code || '',
      autocomplete: 'postal-code',
    });

    const postalField = new FormField(postalInput, {
      label: { text: 'Postal Code' },
    });
    postalCityRow.appendChild(postalField.getElement());

    // City
    const cityInput = createInput({
      name: 'city',
      type: 'text',
      placeholder: 'Zurich',
      value: this.profile?.city || '',
      autocomplete: 'address-level2',
    });

    const cityField = new FormField(cityInput, {
      label: { text: 'City' },
    });
    postalCityRow.appendChild(cityField.getElement());

    container.appendChild(postalCityRow);

    // Country (dropdown, default CH)
    const countrySelect = createSelect({
      name: 'country',
      options: COUNTRY_OPTIONS,
      value: this.profile?.country || 'CH',
    });

    const countryField = new FormField(countrySelect, {
      label: { text: 'Country' },
    });
    container.appendChild(countryField.getElement());
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    const form = this.container.querySelector('#payment-profile-form') as HTMLFormElement;
    if (!form) return;

    // IBAN real-time validation with debounce for better UX
    const ibanInput = form.querySelector('input[name="iban"]') as HTMLInputElement;
    if (ibanInput) {
      // Debounced validation on input (300ms delay to avoid flickering)
      const debouncedValidate = debounce((value: string) => {
        this.validateIbanField(value);
      }, 300);

      ibanInput.addEventListener('input', () => debouncedValidate(ibanInput.value));
      // Immediate validation on blur
      ibanInput.addEventListener('blur', () => this.validateIbanField(ibanInput.value));
    }

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSubmit(form);
    });
  }

  /**
   * Validate IBAN field and update UI
   */
  private validateIbanField(value: string): boolean {
    const result = validateIban(value);

    if (!result.valid && this.ibanField) {
      this.ibanField.setError(result.message || 'Invalid IBAN');
      return false;
    } else if (this.ibanField) {
      this.ibanField.clearValidation();
      return true;
    }

    return result.valid;
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(form: HTMLFormElement): Promise<void> {
    const formData = new FormData(form);

    // Get form values
    const iban = formData.get('iban') as string;
    const swiftBic = formData.get('swiftBic') as string;
    const accountHolderName = formData.get('accountHolderName') as string;
    const streetAddress = formData.get('streetAddress') as string;
    const postalCode = formData.get('postalCode') as string;
    const city = formData.get('city') as string;
    const country = formData.get('country') as string;

    // Validate IBAN
    if (!this.validateIbanField(iban)) {
      return;
    }

    // Validate required fields
    if (!accountHolderName.trim()) {
      showToast({ message: 'Account holder name is required', type: 'error' });
      return;
    }

    // Disable form during save
    const saveButton = this.container.querySelector('#save-button') as HTMLButtonElement;
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.innerHTML = `
        ${createSpinner({ size: 'sm' }).outerHTML}
        <span>Saving...</span>
      `;
    }

    try {
      // Prepare payload - normalize IBAN
      const payload = {
        iban: normalizeIban(iban),
        swiftBic: swiftBic.trim() || undefined,
        accountHolderName: accountHolderName.trim(),
        streetAddress: streetAddress.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        city: city.trim() || undefined,
        country: country || 'CH',
      };

      const response = await apiClient.put<PaymentProfileResponse>('/payment-profiles/me', payload);
      this.profile = response.data.data;

      showToast({ message: 'Payment profile saved successfully', type: 'success' });

      // Update the IBAN input with normalized value
      const ibanInput = form.querySelector('input[name="iban"]') as HTMLInputElement;
      if (ibanInput && this.profile) {
        ibanInput.value = this.profile.iban;
      }

      // Call the callback if provided
      if (this.onSaveCallback && this.profile) {
        this.onSaveCallback(this.profile);
      }
    } catch (error) {
      const err = error as { message?: string };
      showToast({
        message: err.message || 'Failed to save payment profile',
        type: 'error',
      });
    } finally {
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.innerHTML = `
          ${renderIcon('save', { size: 'sm' }).outerHTML}
          <span>Save Profile</span>
        `;
      }
    }
  }
}

/**
 * Create payment profile form component
 */
export function createPaymentProfileForm(
  container: HTMLElement,
  options?: { onSave?: (profile: UserPaymentProfile) => void }
): PaymentProfileForm {
  return new PaymentProfileForm(container, options);
}
